import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const translations = {
  en: {
    langName: "English",
    langToggle: "عربي",
    byline: "by Abdulrahman.",
    role: "Designer",
    desk: "a working desk · Birmingham, UK",
    navPipelines: "Project Pipelines",
    navBuilt: "Built",
    navArchive: "Archive",
    navAbout: "About",
    navContact: "Contact",
    projectPipelines: "Project Pipelines",
    builtProjects: "Built Projects",
    archive: "Archive",
    aboutMe: "About Me",
    contact: "Contact",
    tracing: "tracing 01",
    sheet: "sheet 02",
    folder: "folder 03",
    personal: "personal",
    pinned: "pinned",
    minimized: "minimized · click header to expand",
    footer: "drafting board · v.01 · made with paper, ink & pixels",
    viewProject: "View project",
    projectSheet: "Project sheet",
    mainView: "main view",
    description: "Description",
    details: "Details",
    endSheet: "— end of sheet —",
    no: "No.",
    portfolio: "Portfolio",
    getInTouch: "Get in touch",
    portfolioPrompt: "Enter password to open the portfolio.",
    portfolioWrong: "Incorrect password.",
    pipelinesNote: "Current and ongoing work has been moved into Built Projects so the project archive lives in one place.",
    pipelinesHand: "see Built Projects",
    all: "All",
    sketches: "Sketches",
    models: "Models",
    research: "Research",
    visuals: "Visuals",
    academic: "Academic",
    experiments: "Experiments",
    type: "Type",
    year: "Year",
    stage: "Stage",
    author: "Author",
    built: "Built",
    archiveKind: "Archive",
    leaveNote: "Leave a note",
    notePinned: "Note · pinned",
    noteIntro: "A short message goes a long way. I'll get back to you by email.",
    noteReceived: "Note received.",
    thankYou: "— thank you",
    sendAnother: "Send another",
    name: "Name",
    email: "Email",
    subject: "Subject",
    message: "Message",
    fillRequired: "Please fill in your name, email and a short message.",
    emailError: "Could not open your email app. Please use the email link directly.",
    sendNote: "Send note",
    sending: "Sending",
    closeForm: "Close form",
    close: "Close",
    minimize: "Minimize",
  },
  ar: {
    langName: "العربية",
    langToggle: "EN",
    byline: "بواسطة عبدالرحمن.",
    role: "مصمم",
    desk: "طاولة عمل · برمنغهام، المملكة المتحدة",
    navPipelines: "مسارات المشاريع",
    navBuilt: "المنجز",
    navArchive: "الأرشيف",
    navAbout: "نبذة",
    navContact: "تواصل",
    projectPipelines: "مسارات المشاريع",
    builtProjects: "المشاريع المنجزة",
    archive: "الأرشيف",
    aboutMe: "نبذة عني",
    contact: "تواصل",
    tracing: "مسودة 01",
    sheet: "لوحة 02",
    folder: "ملف 03",
    personal: "شخصي",
    pinned: "مثبت",
    minimized: "مصغر · اضغط على العنوان للتوسيع",
    footer: "لوحة رسم · v.01 · مصنوع بالورق والحبر والبكسلات",
    viewProject: "عرض المشروع",
    projectSheet: "لوحة المشروع",
    mainView: "الصورة الرئيسية",
    description: "الوصف",
    details: "التفاصيل",
    endSheet: "— نهاية اللوحة —",
    no: "رقم",
    portfolio: "البورتفوليو",
    getInTouch: "تواصل معي",
    portfolioPrompt: "أدخل كلمة المرور لفتح البورتفوليو.",
    portfolioWrong: "كلمة المرور غير صحيحة.",
    pipelinesNote: "تم نقل الأعمال الحالية والجارية إلى المشاريع المنجزة حتى يبقى أرشيف المشاريع في مكان واحد.",
    pipelinesHand: "راجع المشاريع المنجزة",
    all: "الكل",
    sketches: "اسكتشات",
    models: "نماذج",
    research: "بحث",
    visuals: "تصورات",
    academic: "أكاديمي",
    experiments: "تجارب",
    type: "النوع",
    year: "السنة",
    stage: "المرحلة",
    author: "المصمم",
    built: "منجز",
    archiveKind: "أرشيف",
    leaveNote: "اترك رسالة",
    notePinned: "ملاحظة · مثبتة",
    noteIntro: "رسالة قصيرة تكفي. سأرد عليك عبر البريد الإلكتروني.",
    noteReceived: "تم استلام الرسالة.",
    thankYou: "— شكرًا لك",
    sendAnother: "إرسال رسالة أخرى",
    name: "الاسم",
    email: "البريد الإلكتروني",
    subject: "الموضوع",
    message: "الرسالة",
    fillRequired: "يرجى كتابة الاسم والبريد الإلكتروني ورسالة قصيرة.",
    emailError: "تعذر فتح تطبيق البريد. يرجى استخدام رابط البريد الإلكتروني مباشرة.",
    sendNote: "إرسال الرسالة",
    sending: "جار الإرسال",
    closeForm: "إغلاق النموذج",
    close: "إغلاق",
    minimize: "تصغير",
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("site-language") || "en");

  useEffect(() => {
    localStorage.setItem("site-language", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    isArabic: lang === "ar",
    setLang,
    toggleLanguage: () => setLang((current) => (current === "ar" ? "en" : "ar")),
    t: (key) => translations[lang][key] || translations.en[key] || key,
  }), [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used inside LanguageProvider");
  return context;
};

export const localized = (item, key, lang) => item?.[`${key}Ar`] && lang === "ar" ? item[`${key}Ar`] : item?.[key];
